import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Grid,
  Card,
  CardContent,
  Typography,
  makeStyles,
  useTheme,
  Divider,
  Button
} from '@material-ui/core'
import DateFilter from '../../components/shared/dateFilter/dateFilter'
import { DateFilters, GlobalNotification } from '../../model'
import moment from 'moment'
import { RegulatoryReportsTypes } from './regulatoryReports.Model'
import CustomFilter from '../../components/shared/customFilter/customFilter'
import {
  generateRegulatoryReport,
  generateRegulatoryReportAsync,
  getGenerateRegulatoryReport
} from './regulatoryReports.service'
import { styles } from './regulatoryReports.styles'
import { useConfirmationDialog } from '../../hooks/useConfirmationDialog'
import { ConfirmationDialogs } from '../../model/enums/confirmationDialogs'
import { RegulatoryReportsPowerDownloadCard } from './regulatoryReportsPowerDownloadCard'
import { enumKeyToValue, enumValueToKey } from '../../helpers/enumHelper'
import useRetries from '../../hooks/useRetries'
import { NotificationMessageIcons } from '../../components'
import { useErrorDetailListDialog } from '../../hooks'
import { useNotify, AwaitedNotification } from '@globaleris/ui.library'
import { EnumGrupoDocumento } from '../../api'

export interface RegulatoryReportsPageProps {
  commodity: number
  onGlobalNotificationChange: (globalNotification: GlobalNotification | undefined) => void
}

export const RegulatoryReportsPowerCommodity: React.FC<RegulatoryReportsPageProps> = props => {
  const [t] = useTranslation(['regulatoryReports'])

  const { getConfirmation, closeConfirmation } = useConfirmationDialog()

  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()

  const [fneecustomFilter, setFneeCustomFilter] = useState<any>(undefined)
  const [escilaCustomFilter, setEscilaCustomFilter] = useState<any>(undefined)

  const [isFetching, setIsFetching] = useState<boolean>(true)

  const [refreshChild, setRefreshChild] = useState<boolean>(true)

  const { showErrorDetailListDialog, errorDetailListDialogProps } = useErrorDetailListDialog()

  const { awaited } = useNotify()

  const [escilaFilter, setEscilaFilter] = useState<any>({
    startDate: moment().startOf('year').subtract(1, 'year').toDate(),
    endDate: moment().endOf('year').subtract(1, 'year').toDate(),
    activeDateFilter: DateFilters.LAST_CLOSED_YEAR
  })

  const [fneeFilter, setFneeFilter] = useState<any>({
    startDate: moment().startOf('year').subtract(1, 'year').toDate(),
    endDate: moment().endOf('year').subtract(1, 'year').toDate(),
    activeDateFilter: DateFilters.LAST_CLOSED_YEAR
  })

  const [priceFilter, setPriceFilter] = useState<any>({
    startDate: moment().subtract(6, 'months').startOf('year').toDate(),
    endDate: moment().subtract(6, 'months').startOf('year').add(6, 'months').toDate(),
    activeDateFilter: DateFilters.FIRS_SEMESTER
  })

  //#region Hooks

  const [startRetriesTimer, stopRetriesTimer, isRetriesTimerActive] = useRetries(
    async () => {
      setTimeout(async () => {
        setRefreshChild(prevState => !prevState)
      }, 7000)
    },
    5000,
    {
      autoStart: false,
      immediate: true,
      onRetry: () => {
        props.onGlobalNotificationChange({
          icon: NotificationMessageIcons.LOADING,
          message: t('regulatoryReports:globalNotifications.retries.messages.onRetry'),
          actions: [
            {
              label: t('regulatoryReports:globalNotifications.retries.actions.cancel'),
              onClick: () => {
                stopRetriesTimer()
                props.onGlobalNotificationChange({
                  message: t('regulatoryReports:globalNotifications.retries.messages.onStop'),
                  actions: [
                    {
                      label: t('regulatoryReports:globalNotifications.retries.actions.refresh'),
                      onClick: async () => {
                        startRetriesTimer()
                      }
                    },
                    {
                      label: t('regulatoryReports:globalNotifications.retries.actions.close'),
                      onClick: () => {
                        props.onGlobalNotificationChange(undefined)
                      }
                    }
                  ]
                })
              }
            }
          ]
        })
      },
      onFinishRetries: () => {
        props.onGlobalNotificationChange({
          icon: NotificationMessageIcons.INFO,
          message: t('regulatoryReports:globalNotifications.retries.messages.onFinish'),
          actions: [
            {
              label: t('regulatoryReports:globalNotifications.retries.actions.refresh'),
              onClick: async () => {
                startRetriesTimer()
              }
            },
            {
              label: t('regulatoryReports:globalNotifications.retries.actions.close'),
              onClick: () => {
                props.onGlobalNotificationChange(undefined)
              }
            }
          ]
        })
      },
      onError: () => {
        props.onGlobalNotificationChange({
          icon: NotificationMessageIcons.ERROR,
          message: t('regulatoryReports:globalNotifications.retries.messages.onError'),
          actions: [
            {
              label: t('regulatoryReports:globalNotifications.retries.actions.refresh'),
              onClick: async () => {
                startRetriesTimer()
              }
            }
          ]
        })
      }
    }
  )

  //#endregion

  //#region Handlers

  const onSelectDateHandler = (
    startDate: Date,
    endDate: Date,
    activeDateFilter?: DateFilters,
    filterType?: RegulatoryReportsTypes
  ): void => {
    switch (filterType) {
      case RegulatoryReportsTypes.ESCILA:
        setEscilaFilter({ startDate, endDate, activeDateFilter })
        setEscilaCustomFilter(undefined)
        break
      case RegulatoryReportsTypes.FNEE:
        setFneeFilter({ startDate, endDate, activeDateFilter })
        setFneeCustomFilter(undefined)
        break
      case RegulatoryReportsTypes.PRICE:
        setPriceFilter({ startDate, endDate, activeDateFilter })
        break
      default:
        break
    }
  }

  const onRemitTableChildFetching = (childIsFetching: boolean) => {
    setIsFetching(childIsFetching)
  }

  const onApplyCustomFilter = async (filterToApply: any, filterType: RegulatoryReportsTypes) => {
    switch (filterType) {
      case RegulatoryReportsTypes.ESCILA:
        setEscilaFilter({
          startDate: filterToApply.startDate,
          endDate: filterToApply.endDate,
          activeDateFilter: DateFilters.CUSTOM
        })
        setEscilaCustomFilter({
          startDate: filterToApply.startDate,
          endDate: filterToApply.endDate
        })
        break
      case RegulatoryReportsTypes.FNEE:
        setFneeFilter({
          startDate: filterToApply.startDate,
          endDate: filterToApply.endDate,
          activeDateFilter: DateFilters.CUSTOM
        })
        setFneeCustomFilter({ startDate: filterToApply.startDate, endDate: filterToApply.endDate })
        break

      default:
        break
    }
  }

  const onPrevalidateCustomFilter = async (filterToApply: any): Promise<boolean> => {
    return true
  }

  //#endregion

  const generateReport = async (reportType: RegulatoryReportsTypes) => {
    let dateFilter
    switch (reportType) {
      case RegulatoryReportsTypes.ESCILA:
        dateFilter = escilaFilter
        break
      case RegulatoryReportsTypes.FNEE:
        dateFilter = fneeFilter
        break
      case RegulatoryReportsTypes.PRICE:
        dateFilter = priceFilter
        break
      default:
        break
    }

    var request = getGenerateRegulatoryReport(
      dateFilter.startDate,
      dateFilter.endDate,
      reportType,
      props.commodity
    )

    getConfirmation(
      ConfirmationDialogs.GENERATE_REPORT,
      t('confirmDialogs:GENERATE_REPORT.message', {
        startDate: moment(dateFilter.startDate).format('DD/MM/YYYY'),
        endDate: moment(dateFilter.endDate).format('DD/MM/YYYY')
      })
    ).then(async () => {
      closeConfirmation()

      awaited({
        title: t('regulatoryReports:notifications.generateReportAsync.title', {}),
        message: t('regulatoryReports:notifications.generateReportAsync.message'),
        keyInfo: reportType.toString()
      } as AwaitedNotification)

      generateRegulatoryReportAsync(request).then(async response => {
        if (response.success) {
          startRetriesTimer()
        }
      })
    })
  }

  return (
    <Grid container spacing={3}>
      <Grid item md={6} lg={4}>
        <Card style={{ height: '100%' }}>
          <CardContent className={classes.cardContent}>
            <div className={classes.cardContentPadding}>
              <Typography variant="subtitle1">
                {t('regulatoryReports:commodityPower.escilaTitle')}
              </Typography>
              <Typography variant="body2" className={classes.description}>
                {t('regulatoryReports:commodityPower.escilaDescription')}
              </Typography>
            </div>
            <div>
              <Divider />
              <div className={classes.cardContentPadding}>
                <Grid container>
                  <div className={classes.dateFilterContainer}>
                    <DateFilter
                      inactive={escilaCustomFilter}
                      enabled={!isFetching}
                      initialFilter={
                        escilaFilter?.activeDateFilter === null ||
                        escilaFilter?.activeDateFilter === undefined
                          ? DateFilters.LAST_CLOSED_YEAR
                          : escilaFilter.activeDateFilter
                      }
                      onSelectDate={(startDate, endDate, activeDateFilter) =>
                        onSelectDateHandler(
                          startDate,
                          endDate,
                          activeDateFilter,
                          RegulatoryReportsTypes.ESCILA
                        )
                      }
                      filters={[
                        {
                          filter: DateFilters.LAST_CLOSED_YEAR,
                          label: t('translation:components.dateFilter.lastClosedYear')
                        }
                      ]}
                    ></DateFilter>
                  </div>
                  <CustomFilter
                    onApplyFilter={filterToApply =>
                      onApplyCustomFilter(filterToApply, RegulatoryReportsTypes.ESCILA)
                    }
                    onPrevalidateFilter={onPrevalidateCustomFilter}
                    appliedFilter={escilaCustomFilter}
                    enabled={!isFetching}
                  ></CustomFilter>
                </Grid>
                <Grid container justify="flex-end" className={classes.buttonContainer}>
                  {/* <Button
                    variant="text"
                    color="primary"
                    disabled={false}
                    onClick={() => {
                      console.log('Ver anteriores');
                    }}
                  >
                    {t('regulatoryReports:commodityPower.seePreviousOption')}
                  </Button> */}
                  <Button
                    variant="text"
                    color="primary"
                    disabled={false}
                    onClick={() => {
                      generateReport(RegulatoryReportsTypes.ESCILA)
                    }}
                  >
                    {t('regulatoryReports:commodityPower.generateReportOption')}
                  </Button>
                </Grid>
              </div>
            </div>
          </CardContent>
        </Card>
      </Grid>
      <Grid item md={6} lg={4}>
        <Card style={{ height: '100%' }}>
          <CardContent className={classes.cardContent}>
            <div className={classes.cardContentPadding}>
              <Typography variant="subtitle1">
                {t('regulatoryReports:commodityPower.fneeTitle')}
              </Typography>
              <Typography variant="body2" className={classes.description}>
                {t('regulatoryReports:commodityPower.fneeDescription')}
              </Typography>
            </div>
            <div>
              <Divider />
              <div className={classes.cardContentPadding}>
                <Grid container>
                  <div className={classes.dateFilterContainer}>
                    <DateFilter
                      inactive={fneecustomFilter}
                      enabled={!isFetching}
                      initialFilter={
                        fneeFilter?.activeDateFilter === null ||
                        fneeFilter?.activeDateFilter === undefined
                          ? DateFilters.LAST_CLOSED_YEAR
                          : fneeFilter.activeDateFilter
                      }
                      onSelectDate={(startDate, endDate, activeDateFilter) =>
                        onSelectDateHandler(
                          startDate,
                          endDate,
                          activeDateFilter,
                          RegulatoryReportsTypes.FNEE
                        )
                      }
                      filters={[
                        {
                          filter: DateFilters.LAST_CLOSED_YEAR,
                          label: t('translation:components.dateFilter.lastClosedYear')
                        }
                      ]}
                    ></DateFilter>
                  </div>
                  <CustomFilter
                    onApplyFilter={filterToApply =>
                      onApplyCustomFilter(filterToApply, RegulatoryReportsTypes.FNEE)
                    }
                    onPrevalidateFilter={onPrevalidateCustomFilter}
                    appliedFilter={fneecustomFilter}
                    enabled={!isFetching}
                  ></CustomFilter>
                </Grid>
                <Grid container justify="flex-end" className={classes.buttonContainer}>
                  {/* <Button
                    variant="text"
                    color="primary"
                    disabled={false}
                    onClick={() => {
                      console.log('Ver anteriores');
                    }}
                  >
                    {t('regulatoryReports:commodityPower.seePreviousOption')}
                  </Button> */}
                  <Button
                    variant="text"
                    color="primary"
                    disabled={false}
                    onClick={() => {
                      generateReport(RegulatoryReportsTypes.FNEE)
                    }}
                  >
                    {t('regulatoryReports:commodityPower.generateReportOption')}
                  </Button>
                </Grid>
              </div>
            </div>
          </CardContent>
        </Card>
      </Grid>
      <Grid item md={6} lg={4}>
        <Card style={{ height: '100%' }}>
          <CardContent className={classes.cardContent}>
            <div className={classes.cardContentPadding}>
              <Typography variant="subtitle1">
                {t('regulatoryReports:commodityPower.priceTitle')}
              </Typography>
              <Typography variant="body2" className={classes.description}>
                {t('regulatoryReports:commodityPower.priceDescription')}
              </Typography>
            </div>
            <div>
              <Divider />
              <div className={classes.cardContentPadding}>
                <Grid container>
                  <div className={classes.dateFilterContainer}>
                    <DateFilter
                      inactive={false}
                      enabled={!isFetching}
                      initialFilter={
                        priceFilter?.activeDateFilter === null ||
                        priceFilter?.activeDateFilter === undefined
                          ? DateFilters.FIRS_SEMESTER
                          : priceFilter.activeDateFilter
                      }
                      onSelectDate={(startDate, endDate, activeDateFilter) =>
                        onSelectDateHandler(
                          startDate,
                          endDate,
                          activeDateFilter,
                          RegulatoryReportsTypes.PRICE
                        )
                      }
                      filters={[
                        {
                          filter: DateFilters.FIRS_SEMESTER,
                          label: t('translation:components.dateFilter.firstSemester')
                        },
                        {
                          filter: DateFilters.SECOND_SEMESTER,
                          label: t('translation:components.dateFilter.secondSemester')
                        },
                        {
                          filter: DateFilters.LAST_CLOSED_YEAR,
                          label: t('translation:components.dateFilter.annual')
                        }
                      ]}
                    ></DateFilter>
                  </div>
                </Grid>
                <Grid container justify="flex-end" className={classes.buttonContainer}>
                  {/* <Button
                    variant="text"
                    color="primary"
                    disabled={false}
                    onClick={() => {
                      console.log('Ver anteriores');
                    }}
                  >
                    {t('regulatoryReports:commodityPower.seePreviousOption')}
                  </Button> */}
                  <Button
                    variant="text"
                    color="primary"
                    disabled={false}
                    onClick={() => {
                      generateReport(RegulatoryReportsTypes.PRICE)
                    }}
                  >
                    {t('regulatoryReports:commodityPower.generateReportOption')}
                  </Button>
                </Grid>
              </div>
            </div>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12}>
        <RegulatoryReportsPowerDownloadCard
          onFetching={onRemitTableChildFetching}
          refreshChild={refreshChild}
          groupDocumentToFilter={[
            enumValueToKey(EnumGrupoDocumento, EnumGrupoDocumento.InformesRegulatoriosPower)
          ]}
        />
      </Grid>
    </Grid>
  )
}
